<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    width="90%"
    max-width="1200px"
  >
    <v-card class="pa-4">
      <v-card-title class="d-flex justify-space-between">
        <span
          class="text-lg-subtitle-1 text-xl-h6 text-uppercase font-weight-black primary--text"
        >
          Non Bonded Bulk Upload
        </span>
        <v-btn depressed fab class="mt-n3" small @click="closeDialog()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row class="pt-8">
          <v-col cols="5" class="d-flex">
            <v-file-input
              v-model="file"
              hide-details
              dense
              label="upload document"
              prepend-inner-icon="mdi-attachment mdi-rotate-90"
              class="mr-4"
              prepend-icon=""
              outlined
              @change="getFileData($event)"
              accept=".xlsx, .xls, .csv"
            />
            <v-icon color="primary" @click="instructionDialog = true">
              mdi-information
            </v-icon>
          </v-col>
          <v-col cols="7" class="d-flex justify-end text-capitalize">
            <v-btn class="primary" depressed @click="downloadSampleExcel()">
              download sample excel file
            </v-btn>
          </v-col>

          <v-col cols="12" class="d-flex justify-space-between pt-0">
            <span class="text-capitalize">
              trip records : {{ totalRecordCount }} &nbsp;&nbsp; container records : {{ totalContainerCount }}
            </span>
            <span>records with error : {{ rowData.length }}</span>
          </v-col>

          <v-col v-if="distinctErrors.length > 0" class="py-0">
            <v-alert border="left" colored-border color="red">
              <v-row no-gutters>
                <v-col class="grow"></v-col>
                <v-col class="shrink">
                  <v-btn class="error" depressed @click="removeAllRecordWithError">
                    remove all with error
                  </v-btn>
                </v-col>
              </v-row>
            </v-alert>
          </v-col>

          <v-col cols="12" v-if="rowData.length > 0">
            <AgGridVue
              @grid-ready="gridReady"
              :grid-options="gridOptions"
              :column-defs="columnDefs"
              :default-col-def="defaultColDef"
              :context="context"
              :row-data="rowData"
              style="width: 100%; height: 400px"
              class="ag-theme-material"
            >
            </AgGridVue>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pa-4 d-flex justify-end">
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!file || rowData.length != 0 || loading"
          @click="submitData()"
        >
          submit
        </v-btn>
      </v-card-actions>
    </v-card>

    <Instruction v-model="instructionDialog" :instructionList="instructionList" />
  </v-dialog>
</template>

<script>
import { read, utils } from "xlsx";
import { AgGridVue } from "ag-grid-vue";
import Instruction from "@/components/General/Instruction.vue";
import RemoveRowButtonBulkUpload from "@/components/General/RemoveRowButtonBulkUpload.vue";
import BulkUploadErrorTooltip from "@/components/General/BulkUploadErrorTooltip.vue";
import { downloadBlobData } from "@/utils/functions.js";
import { nonBondedInstruction } from "@/utils/instruction.js";

const BULK_UPLOAD_URL = "/api/v1/non_bonded_moves/bulk_upload/";

export default {
  components: {
    AgGridVue,
    Instruction,
    RemoveRowButtonBulkUpload,
    BulkUploadErrorTooltip,
  },

  props: {
    value: Boolean,
  },

  data() {
    return {
      loading: false,
      file: null,
      rowData: [],
      columnDefs: [],
      gridApi: null,
      columnApi: null,
      gridOptions: {
        headerHeight: 40,
        rowHeight: 40,
        rowSelection: "multiple",
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
      },
      defaultColDef: {
        lockPosition: true,
      },
      serverErrors: [],
      instructionList: nonBondedInstruction,
      instructionDialog: false,
      dataObject: [],
      containerDataObject: [],
    };
  },

  computed: {
    context() {
      return { parentComponent: this };
    },
    totalRecordCount() {
      return this.dataObject.filter((item) => Object.keys(item).length > 0).length;
    },
    totalContainerCount() {
      return this.containerDataObject.filter((item) => Object.keys(item).length > 0).length;
    },
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    distinctErrors() {
      let err = [];
      this.serverErrors
        .filter((e) => Object.keys(e).length > 0)
        .map((m) => Object.keys(m).map((k) => m[k]))
        .forEach((r) => {
          r.forEach((s) => {
            const values = Array.isArray(s) ? s : [s];
            err = [...new Set([...err, ...values])];
          });
        });
      return err;
    },
  },

  methods: {
    normalizeIfDate(value) {
      if (typeof value !== "string") return value;
      const match = value.trim().match(/^(\d{1,2}[/-]\d{1,2}[/-])(\d{2})$/);
      if (!match) return value;
      const [, prefix, yy] = match;
      const year = Number(yy);
      const fullYear = year <= 68 ? 2000 + year : 1900 + year;
      return `${prefix}${fullYear}`;
    },

    getFileData(file) {
      if (!file) {
        this.clearDialogData();
        return;
      }

      this.loading = true;
      this.$bus.$emit("showLoader", true);

      const reader = new FileReader();

      reader.onload = (e) => {
        setTimeout(() => {
          try {
            const wb = read(e.target.result, {
              type: "array",
              cellDates: false,
              raw: false,
              dateNF: "yyyy-mm-dd",
            });

            const rowData = utils.sheet_to_row_object_array(
              wb.Sheets[wb.SheetNames[0]],
              {
                header: 0,
                defval: "",
                raw: false,
                dateNF: "yyyy-mm-dd",
              }
            );

            this.dataObject = rowData
              .map((row) => {
                const newObject = {};
                for (const [key, value] of Object.entries(row)) {
                  const newKey = key
                    .toString()
                    .toLowerCase()
                    .trim()
                    .replaceAll(" ", "_");
                  newObject[newKey] = this.normalizeIfDate(
                    typeof value === "string" ? value.trim() : value
                  );
                }
                return newObject;
              })
              .filter((row) => row.driver || row.vehicle || row.route)
              .map((row, index) => ({ oldIndex: index, ...row }));

            // Second tab - the unassigned Container No pool. Not every
            // workbook will have it (older downloads, or a user who only
            // filled the first tab), so this is optional.
            const containerSheetName = wb.SheetNames.find(
              (name) => name.toLowerCase() === "containers"
            );
            const containerRowData = containerSheetName
              ? utils.sheet_to_row_object_array(wb.Sheets[containerSheetName], {
                  header: 0,
                  defval: "",
                  raw: false,
                })
              : [];

            this.containerDataObject = containerRowData
              .map((row) => {
                const newObject = {};
                for (const [key, value] of Object.entries(row)) {
                  const newKey = key
                    .toString()
                    .toLowerCase()
                    .trim()
                    .replaceAll(" ", "_");
                  newObject[newKey] = typeof value === "string" ? value.trim() : value;
                }
                return newObject;
              })
              // Only keep rows where the actual tracked fields have a value -
              // a row that's merely styled (border/fill/validation applied to
              // every cell down to row 500, no real data) can otherwise still
              // look "non-empty" depending on what xlsx.js surfaces for it.
              .filter((row) => row.container_no || row.container_type)
              .map((row, index) => ({ oldIndex: index, ...row }));
          } finally {
            this.loading = false;
            this.$bus.$emit("showLoader", false);
          }
        }, 0);
      };

      reader.readAsArrayBuffer(file);
    },

    removeErrorRow(index, oldIndex) {
      setTimeout(() => {
        this.serverErrors.splice(index, 1);
        this.rowData.splice(index, 1);
        this.dataObject.splice(
          this.dataObject.findIndex((item) => item.oldIndex == oldIndex),
          1
        );
      }, 200);
    },

    dataChange(param) {
      let field = param.colDef.field;
      let localError = this.serverErrors[param.node.rowIndex];
      if (param && param.newValue != param.oldValue) {
        delete localError[field];
      }
      let newIndex = this.dataObject.findIndex(
        (item) => item.oldIndex == param.data.oldIndex
      );
      this.dataObject[newIndex] = param.data;

      if (
        (localError && localError.non_field_errors) ||
        (localError && Object.keys(localError) && Object.keys(localError).length == 0)
      ) {
        setTimeout(() => {
          this.serverErrors.splice(param.node.rowIndex, 1);
          this.rowData.splice(param.node.rowIndex, 1);
        }, 200);
      }
      return param.newValue;
    },

    cellStyle(param) {
      if (
        param &&
        param.colDef &&
        param.colDef.field &&
        this.serverErrors &&
        this.serverErrors[param.rowIndex] &&
        this.serverErrors[param.rowIndex][param.colDef.field]
      ) {
        return { backgroundColor: "red" };
      }
      return { backgroundColor: "white" };
    },

    removeAllRecordWithError() {
      while (this.rowData && this.rowData.length > 0) {
        this.removeDataFromRow(this.rowData[0].oldIndex, 0);
      }
    },

    removeDataFromRow(oldIndex, currentIndex) {
      this.dataObject.splice(oldIndex, 1, {});
      this.rowData.splice(currentIndex, 1);

      const data = this.dataObject.filter((obj) => Object.keys(obj).length !== 0);
      if (data.length == 0) {
        this.clearDialogData();
      }
      if (this.rowData.length == 0) {
        this.serverErrors = [];
      }
    },

    gridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
    },

    downloadSampleExcel() {
      this.$api.bulkUpload
        .downloadNonBondedSampleFile()
        .then((res) => {
          downloadBlobData(res, "Sample bulk_non_bonded excel file.xls");
          this.$bus.$emit("showToastMessage", {
            message: "Download file successfully",
            color: "success",
          });
        })
        .catch(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Can't download file!",
            color: "error",
          });
        });
    },

    submitData() {
      this.serverErrors = [];

      const tripRows = this.dataObject.filter((item) => Object.keys(item).length > 0);
      const containerRows = this.containerDataObject.filter((item) => Object.keys(item).length > 0);

      if (tripRows.length === 0 && containerRows.length === 0) {
        this.$bus.$emit("showToastMessage", {
          message: "Data not found!",
          color: "error",
        });
        return;
      }

      const payload = {
        bulk_data: tripRows,
        container_rows: containerRows,
      };

      this.loading = true;
      this.$bus.$emit("showLoader", true);

      this.$api.bulkUpload
        .bulkUploadFile(BULK_UPLOAD_URL, payload)
        .then(() => {
          this.$bus.$emit("showLoader", false);
          this.$emit("refreshList");
          this.loading = false;
          this.$bus.$emit("showToastMessage", {
            message: "Successfully uploaded file",
            color: "success",
          });
          this.serverErrors = [];
          this.closeDialog();
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          if (typeof err == "string") {
            this.$bus.$emit("showToastMessage", { message: err, color: "error" });
          } else if (err && typeof err == "object") {
            const tripErrors = err.bulk_data || [];
            const containerErrors = err.container_rows || [];

            if (tripErrors.length > 0) {
              this.serverErrors = tripErrors;
              this.generateErrorRow(tripErrors);
            }

            if (containerErrors.length > 0) {
              const rows = containerErrors
                .map((e) => `Row ${e.row}: ${e.container_no}`)
                .join(", ");
              this.$bus.$emit("showToastMessage", {
                message: `Container errors - ${rows}`,
                color: "error",
              });
            }

            if (tripErrors.length > 0) {
              this.$bus.$emit("showToastMessage", {
                message: "Error uploading sheet!",
                color: "error",
              });
            }
          }
          this.loading = false;
        });
    },

    generateErrorRow(errorRow) {
      this.columnDefs = Object.keys(this.dataObject[0]).map((item) => ({
        headerName: item.replaceAll("_", " ").toUpperCase(),
        field: item,
        editable: item !== "oldIndex",
        hide: item === "oldIndex" || item === "formError",
        valueParser: this.dataChange,
        cellStyle: this.cellStyle,
      }));

      this.columnDefs.unshift({
        pinned: "left",
        headerName: "errors",
        width: 90,
        cellRendererFramework: "BulkUploadErrorTooltip",
      });

      this.columnDefs.push({
        headerName: "actions",
        pinned: "right",
        width: 90,
        cellRendererFramework: "RemoveRowButtonBulkUpload",
      });

      const sentRows = this.dataObject.filter((item) => Object.keys(item).length > 0);
      const errorsWithRow = errorRow.filter((item) => Object.keys(item).length > 0);

      this.rowData = errorsWithRow
        .map((formError, idx) => {
          const sheetRow = formError.row ?? formError.Row;
          const row = sheetRow != null ? sentRows[Number(sheetRow) - 2] : sentRows[idx];
          return row ? { ...row, formError } : null;
        })
        .filter((row) => row !== null);

      this.serverErrors = this.rowData.map((row) => row.formError);
    },

    closeDialog() {
      this.dialog = false;
      this.clearDialogData();
    },

    clearDialogData() {
      this.file = null;
      this.rowData = [];
      this.columnDefs = [];
      this.dataObject = [];
      this.containerDataObject = [];
      this.serverErrors = [];
      this.loading = false;
    },
  },
};
</script>
